import React, { useState } from 'react';
import { Save, Layout, Globe, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Switch } from './ui/switch';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = [
  { value: 'landing', label: 'Landing Page' },
  { value: 'about', label: 'About Us' },
  { value: 'contact', label: 'Contact' },
  { value: 'blog', label: 'Blog Post' },
  { value: 'product', label: 'Product/Service' },
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'other', label: 'Other' },
];

const SaveAsTemplateModal = ({ open, onClose, pageData, onSaved }) => {
  const { user: currentUser } = useAuth();
  // Effective master admin: a direct Master Admin, OR a Master Admin currently
  // impersonating a tenant (matches backend is_effective_master_admin).
  const isDirectMaster = currentUser?.role === 'master_admin';
  const isImpersonatingAsMaster = currentUser?.impersonator_role === 'master_admin' && !isDirectMaster;
  const canSaveSystemTemplate = isDirectMaster || isImpersonatingAsMaster;
  
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: pageData?.title ? `${pageData.title} Template` : 'My Template',
    description: '',
    category: 'other',
    is_system: false,
    thumbnail_url: '',
  });

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setSaving(true);
    try {
      const templateData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        blocks: pageData.blocks || [],
        page_width: pageData.pageWidth || 'standard',
        theme: pageData.theme,
        is_system: formData.is_system,
        thumbnail_url: formData.thumbnail_url || undefined,
      };

      const res = await api.post('/templates', templateData);
      
      if (formData.is_system) {
        toast.success('Saved as System Template! Available to all accounts.');
      } else {
        toast.success('Page saved as template!');
      }
      
      onSaved?.(res.data);
      onClose();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {formData.is_system ? (
              <>
                <Globe size={20} className="text-emerald-600" />
                Save as System Template
              </>
            ) : (
              <>
                <Layout size={20} />
                Save as Template
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Master Admin: System Template Toggle */}
          {canSaveSystemTemplate && (
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="text-emerald-600" size={20} />
                  <div>
                    <p className="font-medium text-emerald-900">System Template</p>
                    <p className="text-xs text-emerald-700">Available to all accounts</p>
                  </div>
                </div>
                <Switch
                  checked={formData.is_system}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_system: checked })}
                  data-testid="system-template-toggle"
                />
              </div>
              {formData.is_system && isImpersonatingAsMaster && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="design-only-note">
                  <strong>Design only:</strong> you're impersonating this account, so its text, images and links will be replaced with placeholders. Only the layout, theme and styling are saved.
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="template-name">Template Name *</Label>
            <Input
              id="template-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="My Awesome Template"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-description">Description</Label>
            <Textarea
              id="template-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this template is for..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Category</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Thumbnail URL for System Templates */}
          {formData.is_system && (
            <div className="space-y-2">
              <Label htmlFor="thumbnail-url">Thumbnail URL (optional)</Label>
              <Input
                id="thumbnail-url"
                value={formData.thumbnail_url}
                onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                placeholder="https://example.com/thumbnail.jpg"
              />
              <p className="text-xs text-slate-500">Preview image shown in template gallery</p>
            </div>
          )}

          <div className={`rounded-lg p-3 text-sm ${formData.is_system ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-600'}`}>
            <p className="font-medium mb-1">This will save:</p>
            <ul className="list-disc list-inside space-y-1 opacity-80">
              <li>{pageData?.blocks?.length || 0} content blocks</li>
              <li>Page width: {pageData?.pageWidth || 'standard'}</li>
              {pageData?.theme && <li>Custom theme settings</li>}
              {formData.is_system && (
                <li className="font-medium">🌐 Globally available to all accounts</li>
              )}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className={formData.is_system ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          >
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : formData.is_system ? 'Save System Template' : 'Save Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveAsTemplateModal;
